import { toDollarsWithCents } from '@/util/currency';
import {
    Box,
    Input,
    InputGroup,
    InputLeftAddon,
    InputLeftElement,
    InputProps,
} from '@chakra-ui/react';
import { formatPhone, FormattedInputType, formatZipCode } from '@common/inputs';
import { filterValues } from '@common/util/objects';
import { digitsFromStr, toUpperFirst } from '@common/util/strings';
import { toPx } from '@common/util/styles';
import { isNumber, isString, isUndefined } from '@common/util/types';
import { Component, createRef, HTMLInputTypeAttribute, LegacyRef } from 'react';
import WithSliderInput from './WithSliderInput';

export type InputterOptions = {
    preserveEmpty?: boolean;
    toUpperFirst?: boolean;
    isControlled?: boolean;
};

class FormattedInputter<Options extends InputterOptions = InputterOptions> {
    _options: Options | undefined;

    constructor(options?: Options) {
        this._options = options;
    }

    update(oldStr: string, newStr: string): string {
        return this._options?.toUpperFirst ? toUpperFirst(newStr) : newStr;
    }

    toInputString(externalValue: any): string {
        return externalValue || '';
    }

    toExportValue(str: string): any {
        return str;
    }

    areEqual(externalValue: number | string, internalValue: string): boolean {
        return this.toInputString(externalValue) === internalValue;
    }
}

const CURRENCY_REGEX = /^-?\d*\.?\d{0,2}$/im;

type CurrencyInputterOptions = InputterOptions & {
    asNegative?: boolean; // always show negative sign
    min?: number; // minimum integer value
    max?: number; // maximum integer value
};

const parseCurrencyInputString = (
    str: string,
    opts: CurrencyInputterOptions = {}
): { numStr: string; sign: '-' | '' } => {
    const hasPositiveMin = opts.min !== undefined && opts.min >= 0;
    const containsSign = str.indexOf('-') !== -1;
    const isNegative = !hasPositiveMin && (opts.asNegative || containsSign);
    const sign = isNegative ? '-' : '';

    const numStr = str.replace(/[-$,]/g, '');
    return { numStr, sign };
};

/*
    Inserts commas every three dollar digits for amounts >= $1k

    Note: Intl.NumberFormat / our currency.js util do weird things with
    cursor-jumping and usability here; otherwise we'd use one of those.
    Details: https://github.com/plentyfinancial/plenty-app/pull/20/files#r903887984
*/
export const formatCurrencyInputString = (
    numStr: string,
    sign: '-' | ''
): string => {
    const [dollars, cents] = numStr.split('.');
    let newDollars = dollars;
    if (dollars.length > 3) {
        newDollars = '';
        for (let i = dollars.length - 1; i >= 0; i--) {
            const char = dollars[i];
            if (
                newDollars.length >= 3 &&
                newDollars.replaceAll(',', '').length % 3 === 0
            ) {
                newDollars = ',' + newDollars;
            }
            newDollars = char + newDollars;
        }
    }

    // Show "-" instead of "-$0" when the user starts out by typing a minus sign
    if (sign === '-' && newDollars === '0') {
        return '-';
    }

    const formattedNumStr = numStr.includes('.')
        ? newDollars + '.' + cents
        : newDollars;

    return sign + '$' + formattedNumStr;
};

export class CurrencyInputter extends FormattedInputter<CurrencyInputterOptions> {
    update(oldStr: string, newStr: string): string {
        const parsed = parseCurrencyInputString(newStr, this._options);
        newStr = parsed.numStr;

        // Trim leading 0s
        while (newStr.length > 1 && newStr[0] === '0' && newStr[1] !== '.') {
            newStr = newStr.slice(1);
        }

        if (newStr.length === 0) {
            return this._options?.preserveEmpty
                ? ''
                : formatCurrencyInputString('0', parsed.sign);
        }

        if (!CURRENCY_REGEX.test(newStr)) {
            return oldStr;
        }

        const min = this._options?.min ?? -90071992547409; // Number.MIN_SAFE_INTEGER / 100
        const max = this._options?.max ?? 90071992547409; // Number.MAX_SAFE_INTEGER / 100
        const newVal = parseFloat(newStr);
        if (newVal < min) return oldStr;
        if (newVal > max) return oldStr;

        return formatCurrencyInputString(newStr, parsed.sign);
    }

    toInputString(externalValue?: number): string {
        if (this._options?.preserveEmpty && typeof externalValue !== 'number') {
            return '';
        }
        if (!externalValue) return '$0';

        let inputStr = toDollarsWithCents(externalValue);

        // If no cents, remove decimal + trailing zeros
        inputStr = inputStr.replace(/\.00$/, '');
        const parsed = parseCurrencyInputString(inputStr, this._options);
        return formatCurrencyInputString(parsed.numStr, parsed.sign);
    }

    toExportValue(str: string): number | undefined {
        str = str.replaceAll(/[$,]/g, '');
        if (this._options?.preserveEmpty && str === '') return;
        if (str === '' || str === '-') return 0;

        const exportVal = Math.round(parseFloat(str) * 100);

        return this._options?.asNegative ? Math.abs(exportVal) : exportVal;
    }

    areEqual(externalValue: number, internalValue: string): boolean {
        return externalValue === this.toExportValue(internalValue);
    }
}

export type IntegerInputterOptions = InputterOptions & {
    maxDigits?: number; // maximum number of digits (input length)
    min?: number; // minimum integer value
    max?: number; // maximum integer value
};

const INTEGER_REGEX = /^-?\d*$/;

class IntegerInputter extends FormattedInputter<IntegerInputterOptions> {
    update(oldStr: string, newStr: string): string {
        if (newStr.length === 0) return newStr;
        if (!INTEGER_REGEX.test(newStr)) return oldStr;

        const min = this._options?.min;
        const max = this._options?.max;
        const hasMin = min !== undefined;
        const hasMax = max !== undefined;

        // Allow simply "-" if negative values are permitted
        if (newStr === '-') {
            if (hasMin) return min < 0 ? newStr : oldStr;
            return newStr;
        }

        // Clamp min/max if present in options
        const parsed = parseInt(newStr, 10);
        if (isNaN(parsed)) return oldStr;
        if (hasMin && parsed < min) return oldStr;
        if (hasMax && parsed > max) return oldStr;

        // Clamp num digits if maxDigits present in options
        if (
            this._options?.maxDigits !== undefined &&
            this._options?.maxDigits < newStr.length
        ) {
            return oldStr;
        }

        return newStr;
    }

    toInputString(externalValue?: number): string {
        return externalValue ? externalValue.toString() : '';
    }

    toExportValue(str: string): number {
        return parseInt(str, 10);
    }
}

export type FloatInputterOptions = InputterOptions & {
    maxDecimals?: number; // Maximum digits after decimal-point
    min?: number; // minimum float value
    max?: number; // maximum float value
};

const FLOAT_REGEX = /^-?\d*\.?\d*$/;

class FloatInputter extends FormattedInputter<FloatInputterOptions> {
    update(oldStr: string, newStr: string): string {
        if (newStr.length === 0) return newStr;
        if (!FLOAT_REGEX.test(newStr)) return oldStr;

        // Trim leading 0s
        while (newStr.length > 1 && newStr[0] === '0' && newStr[1] !== '.') {
            newStr = newStr.slice(1);
        }

        const min = this._options?.min;
        const max = this._options?.max;
        const hasMin = min !== undefined;
        const hasMax = max !== undefined;

        // Allow simply "-" if negative values are permitted
        if (newStr === '-') {
            if (hasMin) return min < 0 ? newStr : oldStr;
            return newStr;
        }

        // Clamp min/max if present in options
        const parsed = parseFloat(newStr);
        if (hasMin && parsed < min) return oldStr;
        if (hasMax && parsed > max) return oldStr;

        // Clamp decimals if maxDecimals present in options
        const maxDecimals = this._options?.maxDecimals;
        if (newStr.includes('.') && isNumber(maxDecimals)) {
            // Stutter required for typecheck
            const parts = newStr.split('.');
            const intStr = parts[0];
            let decStr = parts[1];
            if (decStr.length > maxDecimals) {
                decStr = decStr.slice(0, maxDecimals);
            }
            newStr = intStr + '.' + decStr;
        }

        return newStr;
    }

    toInputString(externalValue?: number): string {
        if (!externalValue) return '';

        if (this._options?.maxDecimals) {
            const mult = Math.pow(10, this._options?.maxDecimals);
            externalValue = Math.round(externalValue * mult) / mult;
        }
        if (this._options?.max) {
            externalValue = Math.min(externalValue, this._options.max);
        }
        if (this._options?.min) {
            externalValue = Math.max(externalValue, this._options.min);
        }

        return externalValue.toString();
    }

    toExportValue(str: string): number {
        if (str === '' || str === '-') return 0;
        return parseFloat(str) || 0;
    }

    areEqual(externalValue: number, internalValue: string): boolean {
        // Handles case where user has only typed a negative sign
        if (!externalValue && internalValue === '-') return true;

        return externalValue === this.toExportValue(internalValue);
    }
}

const percentInputterDefaultOptions = {
    maxDecimals: 2,
    max: 100,
    min: -100,
};

export class PercentInputter extends FormattedInputter<FloatInputterOptions> {
    constructor(options?: FloatInputterOptions) {
        super({ ...percentInputterDefaultOptions, ...(options ?? {}) });
    }

    update(oldStr: string, newStr: string): string {
        let deletedLastChar = false;
        if (
            oldStr.includes('%') &&
            !newStr.includes('%') &&
            oldStr.replaceAll(/[%]/g, '') === newStr
        ) {
            deletedLastChar = true;
        }
        newStr = newStr.replaceAll(/[%]/g, '');
        if (deletedLastChar) {
            newStr = newStr.slice(0, newStr.length - 1);
        }

        if (newStr.length === 0) {
            return this._options?.preserveEmpty ? '' : '0%';
        }

        if (!newStr.match(FLOAT_REGEX)) return oldStr;

        // Trim leading 0s
        while (newStr.length > 1 && newStr[0] === '0' && newStr[1] !== '.') {
            newStr = newStr.slice(1);
        }

        const min = this._options?.min;
        const max = this._options?.max;
        const hasMin = min !== undefined;
        const hasMax = max !== undefined;

        // Forbid negative sign if negative values aren't permitted
        if (newStr[0] === '-' && hasMin && min >= 0) {
            return oldStr;
        }

        // Clamp min/max if present in options
        const parsed = parseFloat(newStr);
        if (hasMin && parsed < min) return oldStr;
        if (hasMax && parsed > max) return oldStr;

        // Clamp decimals if maxDecimals present in options
        const maxDecimals = this._options?.maxDecimals;
        if (newStr.includes('.') && isNumber(maxDecimals)) {
            // Stutter required for typecheck
            const parts = newStr.split('.');
            const intStr = parts[0];
            let decStr = parts[1];
            if (decStr.length > maxDecimals) {
                decStr = decStr.slice(0, maxDecimals);
            }
            newStr = intStr + '.' + decStr;
        }

        return newStr + '%';
    }

    toInputString(externalValue?: number): string {
        if (!externalValue) return '0%';

        let internalValue = externalValue * 100;

        if (this._options?.maxDecimals) {
            const mult = Math.pow(10, this._options?.maxDecimals);
            internalValue = Math.round(internalValue * mult) / mult;
        }
        if (this._options?.max) {
            internalValue = Math.min(internalValue, this._options.max);
        }
        if (this._options?.min) {
            internalValue = Math.max(internalValue, this._options.min);
        }

        return internalValue.toString() + '%';
    }

    toExportValue(str: string): number | undefined {
        str = str.replaceAll(/[%]/g, '');
        if (this._options?.preserveEmpty && str === '') return;
        if (str === '' || str === '-') return 0;

        return (parseFloat(str) || 0) / 100.0;
    }

    areEqual(externalValue: number, internalValue: string): boolean {
        // Handles case where user has only typed a negative sign
        if (!externalValue && internalValue === '-%') return true;

        return externalValue === this.toExportValue(internalValue);
    }
}

export class PhoneInputter extends FormattedInputter {
    intlCode: string = '';

    update(_oldStr: string, newStr: string): string {
        return formatPhone(newStr);
    }

    toInputString(externalValue: any): string {
        if (!externalValue) return '';
        let internalValue = digitsFromStr(externalValue.toString());

        const hasIntlCodeWithPlus =
            isString(externalValue) && externalValue.startsWith('+');
        const hasIntlCodeAsNum =
            isNumber(externalValue) && externalValue.toString().length > 10;

        if (hasIntlCodeWithPlus || hasIntlCodeAsNum) {
            const intlCodeLen = internalValue.length - 10;
            const intlCode = internalValue.slice(0, intlCodeLen);

            this.intlCode = (hasIntlCodeWithPlus ? '+' : '') + intlCode;
            internalValue = internalValue.slice(intlCodeLen);
        }

        return formatPhone(internalValue);
    }

    toExportValue(str: string): any {
        return this.intlCode + digitsFromStr(str);
    }

    areEqual(externalValue: number, internalValue: string): boolean {
        return this.toInputString(externalValue) === internalValue;
    }
}

export class ZipCodeInputter extends FormattedInputter {
    update(_oldStr: string, newStr: string): string {
        return formatZipCode(newStr);
    }

    toInputString(externalValue: any): string {
        if (!externalValue) return '';
        return formatZipCode(externalValue.toString());
    }

    toExportValue(str: string): string {
        return formatZipCode(str);
    }

    areEqual(externalValue: string, internalValue: string): boolean {
        return this.toInputString(externalValue) === internalValue;
    }
}

export type FormattedInputOptions =
    | CurrencyInputterOptions
    | InputterOptions
    | IntegerInputterOptions
    | FloatInputterOptions;

export interface FormattedInputProps
    extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
    type: FormattedInputType;
    value?: any;
    options?: FormattedInputOptions;
    onChange?: (value: any) => void;
    forwardedRef?: LegacyRef<HTMLInputElement>;
    slider?: SliderConfig;
    iconElement?: JSX.Element;
    iconAddon?: JSX.Element;
}

export interface SliderConfig {
    min: number;
    max: number;
    step: number;
    scaleWhenActive?: number;
    nonlinear?: boolean;
}

export interface FormattedInputState {
    value: string;
    focused: boolean;
}

export default class FormattedInput extends Component<
    FormattedInputProps,
    FormattedInputState
> {
    _inputter:
        | FormattedInputter
        | CurrencyInputter
        | IntegerInputter
        | FloatInputter
        | PercentInputter
        | PhoneInputter;
    _inputType: string;
    _baseProps: InputProps;
    _inputRef: LegacyRef<HTMLInputElement>;

    constructor(props: FormattedInputProps) {
        super(props);
        const inputProps = this._getInputProps(props.type, props.options);
        this._inputter = inputProps.inputter;
        this._inputType = inputProps.inputType;
        this._baseProps = inputProps.baseProps ?? {};
        this._inputRef = props.forwardedRef ?? createRef<HTMLInputElement>();

        this.state = {
            value: this._inputter.toInputString(props.value),
            focused: false,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: FormattedInputProps) {
        if (nextProps.type !== this.props.type) {
            const inputProps = this._getInputProps(
                nextProps.type,
                nextProps.options
            );
            this._inputter = inputProps.inputter;
            this._inputType = inputProps.inputType;

            this.setState({
                value: this._inputter.toInputString(nextProps.value),
            });
            // eslint-disable-next-line
        } else if (nextProps.value != this.props.value) {
            if (!this._inputter.areEqual(nextProps.value, this.state.value)) {
                this.setState({
                    value: this._inputter.toInputString(nextProps.value),
                });
            }
        }
    }

    render() {
        const {
            type, // eslint-disable-line @typescript-eslint/no-unused-vars
            value, // eslint-disable-line @typescript-eslint/no-unused-vars
            options, // eslint-disable-line @typescript-eslint/no-unused-vars
            onChange, // eslint-disable-line @typescript-eslint/no-unused-vars
            forwardedRef,
            slider, // eslint-disable-line @typescript-eslint/no-unused-vars
            iconElement,
            iconAddon,
            ...rest
        } = this.props;

        const restProps = {
            ...this._baseProps,
            ...filterValues(rest ?? {}, (v) => !isUndefined(v)),
        };

        if (this.props.slider) {
            return (
                <Box position="relative">
                    <Input
                        {...restProps}
                        type={this._inputType}
                        value={this.state.value}
                        onChange={this._handleChange}
                        ref={this._inputRef}
                        focusBorderColor="transparent"
                        borderBottomColor="transparent"
                    />
                    <WithSliderInput
                        min={this.props.slider.min}
                        max={this.props.slider.max}
                        step={this.props.slider.step}
                        nonlinear={this.props.slider.nonlinear}
                        scaleWhenActive={this.props.slider.scaleWhenActive}
                        value={parseFloat(this.props.value)}
                        onChange={(value) => {
                            this._handleChange({
                                target: {
                                    value: this._inputter.toInputString(value),
                                } as HTMLInputElement,
                            });
                            // If user reaches end of slider,
                            // select input text to convey that it's editable.
                            // Short timeout ensures default input focus
                            // behavior is overridden.
                            if (
                                value === this.props.slider?.max &&
                                this._inputRef &&
                                typeof this._inputRef === 'object' &&
                                'current' in this._inputRef
                            ) {
                                const inputRef = this._inputRef.current;
                                const selectInput = () => {
                                    inputRef?.select();
                                };
                                setTimeout(selectInput, 100);
                            }
                        }}
                        position="absolute"
                        bottom={toPx(-7)}
                        left={0}
                        right={0}
                    />
                </Box>
            );
        }

        return (
            <InputGroup variant={restProps.variant} size={restProps.size}>
                {iconElement && (
                    <InputLeftElement pointerEvents="none">
                        {iconElement}
                    </InputLeftElement>
                )}
                {iconAddon && (
                    <InputLeftAddon pointerEvents="none">
                        {iconAddon}
                    </InputLeftAddon>
                )}
                <Input
                    {...restProps}
                    type={this._inputType}
                    value={this.state.value}
                    onChange={this._handleChange}
                    ref={forwardedRef}
                />
            </InputGroup>
        );
    }

    _handleChange = (e: { target: HTMLInputElement }) => {
        const value = this._inputter.update(this.state.value, e.target.value);
        if (this.props.options?.isControlled) {
            this.props.onChange?.(value);
            return;
        }
        if (value === this.state.value) return;
        this.setState({ value }, () => {
            if (this.props.onChange) {
                this.props.onChange(this._inputter.toExportValue(value));
            }
        });
    };

    _getInputProps(
        type: FormattedInputType,
        options?: FormattedInputOptions
    ): {
        inputter: FormattedInputter;
        inputType: HTMLInputTypeAttribute;
        baseProps?: InputProps;
    } {
        switch (type) {
            case 'currency':
                return {
                    inputter: new CurrencyInputter(options),
                    inputType: 'text',
                    baseProps: {
                        placeholder: '$0',
                    },
                };
            case 'integer':
                return {
                    inputter: new IntegerInputter(options),
                    inputType: 'text',
                };
            case 'float':
                return {
                    inputter: new FloatInputter(options),
                    inputType: 'text',
                };
            case 'percent':
                return {
                    inputter: new PercentInputter(options),
                    inputType: 'text',
                };
            case 'email': {
                return {
                    inputter: new FormattedInputter(options),
                    inputType: 'email',
                };
            }
            case 'phone': {
                return {
                    inputter: new PhoneInputter(options),
                    inputType: 'tel',
                };
            }
            case 'zipCode':
                return {
                    inputter: new ZipCodeInputter(options),
                    inputType: 'text',
                    baseProps: {
                        placeholder: '12345',
                        maxLength: 5,
                    },
                };
            case 'text':
            default:
                return {
                    inputter: new FormattedInputter(options),
                    inputType: 'text',
                };
        }
    }
}
